import { api } from "@/lib/api";
import {
  RepeatedAppointmentWithDetails,
  RepeatedAppointmentInput,
  RepeatedAppointmentUpdateInput,
  RepeatedAppointmentSingleResponse,
  RepeatedAppointmentListResponse,
} from "@/types/repeatedAppointment.types";

/**
 * Service for managing RepeatedAppointment entities
 * These store recurring appointment preferences: packageId, vehicleIds, preferredDay, preferredTime
 */
export const repeatedAppointmentService = {
  /**
   * Create a new RepeatedAppointment
   * Used when setting up recurring appointments for a customer
   */
  async create(
    data: RepeatedAppointmentInput
  ): Promise<RepeatedAppointmentWithDetails> {
    try {
      const response = await api.post<RepeatedAppointmentSingleResponse>(
        "/repeated-appointments",
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("Error creating repeated appointment:", error);
      throw error;
    }
  },

  /**
   * Get a single RepeatedAppointment by ID
   * Returns full details including package, vehicles, and appointment count
   */
  async getById(id: string): Promise<RepeatedAppointmentWithDetails> {
    try {
      const response = await api.get<RepeatedAppointmentSingleResponse>(
        `/repeated-appointments/${id}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching repeated appointment:", error);
      throw error;
    }
  },

  /**
   * Get all RepeatedAppointments for a customer
   * Returns array of setups with package and vehicle details
   */
  async getByCustomerId(
    customerId: string
  ): Promise<RepeatedAppointmentWithDetails[]> {
    try {
      const response = await api.get<RepeatedAppointmentListResponse>(
        `/repeated-appointments/customer/${customerId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching customer repeated appointments:", error);
      throw error;
    }
  },

  /**
   * Update a RepeatedAppointment's preferences
   * Can update: preferredDay, preferredTime, vehicleIds, packageId
   *
   * Note: Changes affect future cron-generated appointments, not existing ones
   */
  async update(
    id: string,
    data: RepeatedAppointmentUpdateInput
  ): Promise<RepeatedAppointmentWithDetails> {
    try {
      const response = await api.put<RepeatedAppointmentSingleResponse>(
        `/repeated-appointments/${id}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating repeated appointment:", error);
      throw error;
    }
  },

  /**
   * Soft delete a RepeatedAppointment
   * This will stop future appointments from being generated for this setup
   */
  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/repeated-appointments/${id}`);
    } catch (error) {
      console.error("Error deleting repeated appointment:", error);
      throw error;
    }
  },
};
